import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';

export const useSizeIssueReport = (orderId, orderNumber) => {
  const [sizeIssueReport, setSizeIssueReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!orderId && !orderNumber) {
      setLoading(false);
      return;
    }

    const checkSizeIssueReport = async () => {
      try {
        setLoading(true);
        setError(null);

        // Query by orderNumber first (works for both logged-in and guest users)
        // Then filter by userId on the client side for logged-in users
        let q;
        if (orderNumber) {
          q = query(
            collection(db, 'sizeIssues'),
            where('orderNumber', '==', orderNumber),
            limit(10) // Get more results to filter on client side
          );
        } else if (orderId && currentUser) {
          // Fallback: if we only have orderId, try to query by userId first
          // This is less efficient but works when orderNumber is not available
          q = query(
            collection(db, 'sizeIssues'),
            where('userId', '==', currentUser.uid),
            limit(20) // Get more results to filter by orderId
          );
        }

        if (q) {
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            let matchingReports = [];
            
            // Filter results based on user authentication and order matching
            for (const doc of querySnapshot.docs) {
              const data = doc.data();
              
              // Check if this report matches the order we're looking for
              const matchesOrder = (orderId && data.orderId === orderId) || 
                                 (orderNumber && data.orderNumber === orderNumber);
              
              if (!matchesOrder) continue;
              
              // For logged-in users, check if the report belongs to them
              if (currentUser) {
                if (data.userId === currentUser.uid) {
                  matchingReports.push({
                    id: doc.id,
                    ...data
                  });
                }
              } else {
                // For guest users, add all matching reports
                matchingReports.push({
                  id: doc.id,
                  ...data
                });
              }
            }
            
            // Sort by submittedAt (most recent first) and take the first one
            if (matchingReports.length > 0) {
              matchingReports.sort((a, b) => {
                const aTime = a.submittedAt?.seconds || 0;
                const bTime = b.submittedAt?.seconds || 0;
                return bTime - aTime; // Most recent first
              });
              setSizeIssueReport(matchingReports[0]);
            } else {
              setSizeIssueReport(null);
            }
          } else {
            setSizeIssueReport(null);
          }
        }
      } catch (err) {
        setError(err);
        // If there's a permission error, just set no report found
        if (err.code === 'permission-denied') {
          setSizeIssueReport(null);
        }
      } finally {
        setLoading(false);
      }
    };

    checkSizeIssueReport();
  }, [orderId, orderNumber, currentUser]);

  return { sizeIssueReport, loading, error };
};
